/**
 * Il ledger: quanto rende davvero un immobile, e a chi.
 *
 * Due idee reggono tutto il resto di questo file.
 *
 * **1. Un costo ha sempre due domande, non una.** Chi lo *sopporta* e chi lo
 * *anticipa* sono cose diverse: l'idraulico di domenica lo paghi tu in contanti
 * anche quando tocca al proprietario. Tenerle separate è il motivo per cui a
 * fine mese si può dire "il proprietario ti deve 240€" invece di litigare.
 *
 * **2. Ogni voce dichiara da dove viene.** Lo stesso costo arriva da più parti —
 * il task chiuso su Otto, la spesa sul gestionale, la fattura dell'impresa — e
 * un ledger che lo conta due volte sbaglia del 200% sulla voce più grossa.
 * `SorgenteCosto` porta l'affidabilità dentro il dato, e la riconciliazione la
 * usa per scegliere. Un P&L che non sa dire quanto è stimato non è spendibile.
 */

import { z } from "zod";
import type { Cents } from "./money.js";
import type { IsoDate } from "./dates.js";

// ── Il rapporto economico sull'immobile ──────────────────────────────────────

/**
 * Chi sei rispetto a quella casa.
 *
 * Non è "proprietario sì/no": l'host spesso non è il proprietario. Lo stesso
 * appartamento, con gli stessi ospiti e lo stesso incasso, lascia in tasca
 * numeri diversi in ordine di grandezza a seconda di quale di questi tre è.
 */
export const TIPI_RAPPORTO = ["proprieta", "mandato", "affitto"] as const;
export type TipoRapporto = (typeof TIPI_RAPPORTO)[number];

export const ETICHETTE_RAPPORTO: Record<TipoRapporto, string> = {
  proprieta: "È mio",
  mandato: "In gestione per conto di",
  affitto: "In affitto, lo subaffitto",
};

/**
 * Su quale cifra si calcola la fee di gestione.
 *
 * Tre percentuali diverse sullo stesso mese danno tre numeri diversi, e
 * l'accordo che il property manager ha in testa è **una** di queste, non una
 * media. `netto_canale` è la più diffusa in Italia e mancava: è il 30% su
 * quello che il canale ha davvero versato, cioè lordo meno commissioni — né il
 * lordo pieno, né l'utile dopo pulizie e utenze.
 */
export const BASI_FEE = ["lordo", "netto_canale", "utile"] as const;
export type BaseFee = (typeof BASI_FEE)[number];

export const ETICHETTE_BASE_FEE: Record<BaseFee, string> = {
  lordo: "sui ricavi lordi",
  netto_canale: "sul netto dalle OTA",
  utile: "sull'utile della casa",
};

/** Le parti che possono sopportare o anticipare un costo. */
export const PARTI = ["gestore", "proprietario", "ospite"] as const;
export type Parte = (typeof PARTI)[number];

export const ETICHETTE_PARTE: Record<Parte, string> = {
  gestore: "Tu",
  proprietario: "Il proprietario",
  ospite: "L'ospite",
};

// ── Categorie di costo ───────────────────────────────────────────────────────

/**
 * Le voci che coprono la quasi totalità dei costi di un affitto breve.
 *
 * `commissioni_ota`, `canone` e `gestione` non sono costi operativi della casa
 * ma **trasferimenti** fra le parti: vedi `CATEGORIE_TRASFERIMENTO`.
 */
export const CATEGORIE_COSTO = [
  "commissioni_ota",
  "imposte",
  "pulizie",
  "biancheria",
  "utenze",
  "manutenzione",
  "tassa_soggiorno",
  "condominio",
  "assicurazione",
  "canone",
  "gestione",
  "altro",
] as const;
export type CategoriaCosto = (typeof CATEGORIE_COSTO)[number];
export const CategoriaCostoSchema = z.enum(CATEGORIE_COSTO);

export const ETICHETTE_CATEGORIA: Record<CategoriaCosto, string> = {
  commissioni_ota: "Commissioni e trattenute del canale",
  imposte: "Imposte",
  pulizie: "Pulizie",
  biancheria: "Biancheria",
  utenze: "Utenze",
  manutenzione: "Manutenzione",
  tassa_soggiorno: "Imposta di soggiorno da versare",
  condominio: "Condominio",
  assicurazione: "Assicurazione",
  canone: "Canone di locazione",
  gestione: "Fee di gestione",
  altro: "Altro",
};

/**
 * Non sono costi della casa: sono passaggi di denaro fra gestore e proprietario.
 *
 * Vanno esclusi dall'utile della casa — altrimenti il canone che paghi al
 * proprietario farebbe sembrare l'immobile meno redditizio di quanto è — e
 * riappaiono nella ripartizione, che è il loro posto.
 */
export const CATEGORIE_TRASFERIMENTO: readonly CategoriaCosto[] = [
  "canone",
  "gestione",
];

export function eTrasferimento(categoria: CategoriaCosto): boolean {
  return CATEGORIE_TRASFERIMENTO.includes(categoria);
}

/** Categorie che ricorrono uguali ogni periodo: una sola riga vera per mese. */
export const CATEGORIE_RICORRENTI: readonly CategoriaCosto[] = [
  "utenze",
  "condominio",
  "assicurazione",
  "canone",
];

// ── Provenienza e affidabilità ───────────────────────────────────────────────

/**
 * Da dove viene un numero. Dichiararlo non è pignoleria contabile: è la
 * differenza fra un P&L che si può mandare a un proprietario e uno che no.
 */
/**
 * Come è messo l'host con l'IVA e le imposte.
 *
 * `privato` = nessuna partita IVA. `forfettario` = partita IVA in regime
 * forfettario. `ordinario` = regime ordinario, società comprese. Solo
 * l'ordinario recupera l'IVA sulle commissioni delle piattaforme: un privato
 * se la vede addebitare, un forfettario la deve in reverse charge e non la
 * può detrarre.
 */
export const REGIMI_FISCALI = ["privato", "forfettario", "ordinario"] as const;
export type RegimeFiscale = (typeof REGIMI_FISCALI)[number];
export const RegimeFiscaleSchema = z.enum(REGIMI_FISCALI);

export const ETICHETTE_REGIME: Record<RegimeFiscale, string> = {
  privato: "Privato, senza partita IVA",
  forfettario: "Partita IVA in regime forfettario",
  ordinario: "Regime ordinario o società",
};

export const SORGENTI_COSTO = [
  "pms",
  "documento",
  "manuale",
  "confermato",
  "calcolato",
  "stimato",
] as const;
export type SorgenteCosto = (typeof SORGENTI_COSTO)[number];

export const ETICHETTE_SORGENTE: Record<SorgenteCosto, string> = {
  pms: "dal gestionale",
  documento: "da una ricevuta",
  manuale: "inserito a mano",
  confermato: "confermato da te",
  calcolato: "calcolato",
  stimato: "stimato",
};

/**
 * Quanto ci si può fidare, in scala. Serve alla riconciliazione: quando due
 * righe parlano dello stesso fatto, vince la più alta.
 */
const AFFIDABILITA: Record<SorgenteCosto, number> = {
  documento: 5,
  pms: 5,
  manuale: 4,
  confermato: 4,
  calcolato: 2,
  stimato: 1,
};

export function affidabilita(sorgente: SorgenteCosto): number {
  return AFFIDABILITA[sorgente];
}

/** Una stima è un numero che Otto si è inventato: va sempre segnalata. */
export function eStimato(sorgente: SorgenteCosto): boolean {
  return sorgente === "stimato";
}

// ── Il mese di competenza ────────────────────────────────────────────────────

/**
 * "AAAA-MM".
 *
 * Il P&L si ragiona per **competenza**, non per incasso: una prenotazione fatta
 * a gennaio, pagata a marzo, per un soggiorno a luglio è profitto di luglio.
 * È il motivo per cui i fogli Excel dei property manager sono quasi sempre
 * sbagliati, e per noi è gratis perché il calendario è già per notte.
 */
export type Mese = string;

const MESE_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isMese(value: unknown): value is Mese {
  return typeof value === "string" && MESE_RE.test(value);
}

export const MeseSchema = z
  .string()
  .regex(MESE_RE, "Il mese va scritto come AAAA-MM");

export function meseDi(date: IsoDate): Mese {
  return date.slice(0, 7);
}

export function primoGiornoDelMese(mese: Mese): IsoDate {
  return `${mese}-01`;
}

export function ultimoGiornoDelMese(mese: Mese): IsoDate {
  const [anno, m] = mese.split("-").map(Number) as [number, number];
  // Il giorno 0 del mese successivo è l'ultimo di questo.
  const d = new Date(Date.UTC(anno, m, 0));
  return d.toISOString().slice(0, 10);
}

export function spostaMese(mese: Mese, delta: number): Mese {
  const [anno, m] = mese.split("-").map(Number) as [number, number];
  const totale = anno * 12 + (m - 1) + delta;
  const nuovoAnno = Math.floor(totale / 12);
  const nuovoMese = (totale % 12) + 1;
  return `${nuovoAnno}-${String(nuovoMese).padStart(2, "0")}`;
}

const NOMI_MESE = [
  "gennaio",
  "febbraio",
  "marzo",
  "aprile",
  "maggio",
  "giugno",
  "luglio",
  "agosto",
  "settembre",
  "ottobre",
  "novembre",
  "dicembre",
];

/**
 * "luglio" — il nome del mese, senza l'anno.
 *
 * Prende sia `2026-07` sia `2026-07-15`: le due forme convivono nel prodotto
 * (i conti ragionano a mesi, il calendario a giorni) e chi vuole scrivere il
 * nome del mese non deve sapere quale ha in mano. Questa tabella era stata
 * ricopiata a mano in altri due file, e una terza copia è arrivata a leggere
 * la data con l'orologio sbagliato: «luglio» usciva «giugno».
 */
export function nomeMese(mese: Mese | string): string {
  return NOMI_MESE[Number(mese.slice(5, 7)) - 1] ?? mese;
}

/** "luglio 2026" */
export function formatMese(mese: Mese): string {
  const [anno, m] = mese.split("-").map(Number) as [number, number];
  return `${NOMI_MESE[m - 1]} ${anno}`;
}

// ── Le entità ────────────────────────────────────────────────────────────────

/**
 * Chi paga cosa, deciso una volta e da fermi.
 *
 * Le rotture sono l'unica voce in cui il pagante è davvero contestato — un wc,
 * una tapparella che cede per usura — e la discussione arriva sempre *dopo*,
 * con la fattura sul tavolo e tutti e due con un motivo per tirare dalla propria
 * parte. Questa mappa sposta la decisione a prima, quando nessuno dei due ha
 * ancora un interesse in gioco.
 */
export type ImputazioniDefault = Partial<Record<CategoriaCosto, Parte>>;

export interface RapportoView {
  id: string;
  listingId: string;
  tipo: TipoRapporto;
  /** Il nome del proprietario. `null` quando l'immobile è tuo. */
  controparte: string | null;
  /** Mandato: percentuale in punti base (3000 = 30%). Mai in virgola mobile. */
  feePercentualeBp: number | null;
  /** Su cosa si applica la percentuale. Senza, non significa niente. */
  baseFee: BaseFee;
  /** Si somma alla percentuale: "il 15% più 30€ al mese" è un accordo reale. */
  feeFissaCents: Cents | null;
  /** Affitto: quanto paghi al proprietario ogni mese. */
  canoneMensileCents: Cents | null;
  imputazioni: ImputazioniDefault;
  /**
   * Sotto questa cifra la manutenzione la paghi tu, sopra il proprietario.
   * È l'accordo più diffuso perché è l'unico che non lascia interpretazioni.
   */
  sogliaManutenzioneCents: Cents | null;
  /** Sotto questa cifra non vale la pena aprire una richiesta danni all'ospite. */
  sogliaReclamoCents: Cents | null;
  attivoDal: IsoDate;
  attivoAl: IsoDate | null;
}

/**
 * Una riga di costo.
 *
 * `dedupeKey` sta sul **fatto osservato** — questa bolletta, questo turnover —
 * non sulla riga: le derivazioni rigirano a ogni sincronizzazione e non devono
 * sfornare doppioni, esattamente come per gli avvisi.
 */
export interface VoceCosto {
  id: string;
  listingId: string;
  categoria: CategoriaCosto;
  descrizione: string;
  importoCents: Cents;
  /** Il mese a cui il costo appartiene, che non è quello in cui è stato pagato. */
  competenza: IsoDate;
  sorgente: SorgenteCosto;
  aCaricoDi: Parte;
  /** Chi ha tirato fuori i soldi. Diverso da `aCaricoDi` genera un saldo. */
  anticipatoDa: Parte;
  dedupeKey: string;
  /** L'id della cosa da cui il costo nasce: il task, la prenotazione, la fattura. */
  origine: string | null;
  /**
   * Una stima che **completa** i numeri veri invece di sostituirli.
   *
   * Di regola una stima si fa da parte appena esiste un numero vero per quella
   * categoria in quel mese: Otto tira a indovinare finché non sa, e quando sa
   * smette. Ma la commissione stimata sulle notti che nessuna prenotazione
   * copre riguarda *altre notti*, non le stesse: in un mese metà coperto e
   * metà no, farla sparire toglierebbe dal conto proprio la metà di cui non
   * sappiamo niente, e il mese sembrerebbe costare la metà.
   */
  complementare?: boolean;
}

/** Una voce non ancora salvata: la producono le derivazioni e le proposte. */
export type NuovaVoceCosto = Omit<VoceCosto, "id">;

// ── Il risultato ─────────────────────────────────────────────────────────────

export interface RigaProfitto {
  categoria: CategoriaCosto;
  etichetta: string;
  importoCents: Cents;
  sorgente: SorgenteCosto;
  aCaricoDi: Parte;
  /** Quante voci sono confluite in questa riga. */
  voci: number;
}

/**
 * Il P&L di un immobile per un mese, nelle due viste che servono davvero.
 *
 * Sono numeri diversi e rispondono a due domande diverse: *questa casa rende?*
 * (per decidere se tenerla) e *a me cosa resta?* (per decidere se il lavoro che
 * ci metto ha senso). Su un mandato divergono di parecchio, e confonderli è il
 * modo tipico di lavorare in perdita senza accorgersene.
 */
/**
 * Un canale nel mese: quanto ha versato, quanto ha trattenuto, e se l'ha
 * trattenuto prima o dopo.
 *
 * È la distinzione che fa quadrare il conto con Guesty (verificato l'11/9/2026
 * sui tre immobili): Airbnb versa già al netto della sua commissione, Booking
 * versa tutto e fattura la commissione a parte. Sommare i payout dà il numero
 * che Guesty chiama «ricavi lordi»; togliere anche la commissione Airbnb la
 * toglierebbe due volte.
 */
export interface CanaleDelMese {
  canale: string;
  prenotazioni: number;
  /** Quanto il canale ha versato (il payout). */
  incassatoCents: Cents;
  /**
   * Quello che gli ospiti hanno pagato su questo canale, senza imposta di
   * soggiorno: payout, più la commissione che il canale ha già trattenuto,
   * meno l'imposta di soggiorno che arriva dentro il payout.
   */
  vendutoLordoCents: Cents;
  /** La commissione del canale, dichiarata dal gestionale o stimata. */
  commissioneCents: Cents;
  /** Il costo di incasso, dove il canale lo addebita. */
  incassoCents: Cents;
  /** `true` se la commissione era già tolta dal payout (Airbnb). */
  trattenutaNelPayout: boolean;
  /** Imposta di soggiorno arrivata dentro il payout, da versare al Comune. */
  impostaSoggiornoDaVersareCents: Cents;
  /** Imposta di soggiorno incassata e versata dal canale al posto tuo. */
  impostaSoggiornoCanaleCents: Cents;
  /** Almeno una commissione è stimata, non dichiarata. */
  stimato: boolean;
  /** Almeno un costo di incasso è stimato: il gestionale dichiarava zero. */
  incassoStimato: boolean;
  /**
   * Sappiamo per certo se questo canale versa al lordo o al netto?
   *
   * Su Airbnb e Booking sì (verificato sull'account vero il 9/9/2026). Su un
   * canale che non abbiamo mai visto no, e il conto lo tratta come un canale
   * che versa al lordo: la commissione dichiarata si toglie una volta sola.
   * Se poi si scoprisse che versa già al netto, il mese risulterebbe meno
   * redditizio di quanto è — mai il contrario, e questo flag lo dichiara.
   */
  convenzioneVerificata: boolean;
}

export interface RendicontoCanali {
  /** La somma dei payout: il numero che Guesty chiama «ricavi lordi». */
  incassatoCents: Cents;
  /**
   * Quello che hanno pagato gli ospiti, senza imposta di soggiorno.
   *
   * Incassato **più** le commissioni già trattenute (Airbnb versa al netto)
   * e **meno** l'imposta di soggiorno che arriva dentro il payout (Booking la
   * incassa per il Comune, non è venduto di nessuno). Senza quella
   * sottrazione l'etichetta «senza imposta di soggiorno» era falsa di tutta
   * la city tax: su una fixture da 41.590 il numero vero è 39.310.
   */
  vendutoLordoCents: Cents;
  commissioniTrattenuteCents: Cents;
  commissioniDaPagareCents: Cents;
  incassoCents: Cents;
  impostaSoggiornoDaVersareCents: Cents;
  impostaSoggiornoCanaleCents: Cents;
  /** L'IVA sulle commissioni che l'host non recupera; zero in regime ordinario. */
  ivaCommissioniCents: Cents;
  /** Incassato meno commissioni da pagare, incasso e imposta di soggiorno da versare. */
  primaDiPulizieEImposteCents: Cents;
  canali: CanaleDelMese[];
}

/**
 * Come sono contate le due grandezze che stanno una accanto all'altra.
 *
 * I ricavi per **check-in**, come fa Guesty: una prenotazione appartiene
 * tutta intera al mese in cui l'ospite entra. Le notti per **calendario**:
 * quelle che cadono dentro il mese. Su un soggiorno a cavallo i due conti
 * non parlano della stessa cosa — 62 notti dal 17 maggio danno a maggio
 * tutto il venduto e a giugno trenta notti vendute e zero euro — e chi legge
 * due numeri incoerenti senza che nessuno glielo dica smette di fidarsi di
 * entrambi.
 */
export interface ConvenzioniConto {
  ricavi: "check_in";
  notti: "calendario";
}

export const CONVENZIONI_CONTO: ConvenzioniConto = {
  ricavi: "check_in",
  notti: "calendario",
};

export const RENDICONTO_VUOTO: RendicontoCanali = {
  incassatoCents: 0,
  vendutoLordoCents: 0,
  commissioniTrattenuteCents: 0,
  commissioniDaPagareCents: 0,
  incassoCents: 0,
  impostaSoggiornoDaVersareCents: 0,
  impostaSoggiornoCanaleCents: 0,
  ivaCommissioniCents: 0,
  primaDiPulizieEImposteCents: 0,
  canali: [],
};

export interface ProfittoImmobile {
  listingId: string;
  nickname: string;
  mese: Mese;
  tipoRapporto: TipoRapporto;
  controparte: string | null;

  nottiVendute: number;
  /** I giorni del mese, tutti. */
  nottiDisponibili: number;
  /**
   * Di quanti giorni abbiamo davvero il calendario.
   *
   * La cache non copre tutto il passato: senza questo numero un mese di cui
   * sappiamo otto giorni sembrerebbe un mese andato male, invece che un mese
   * che non conosciamo. È la stessa regola della ricevuta — meglio dire "non
   * lo so" che dire un numero credibile e sbagliato.
   */
  nottiConosciute: number;

  /**
   * L'incassato del mese per check-in: coincide con i «ricavi lordi» di Guesty.
   *
   * Zero quando in questo mese non entra nessuno, e zero è la risposta giusta:
   * il ripiego sul listino delle notti occupate contava la stessa
   * prenotazione in tre mesi diversi, due volte a prezzo pieno.
   */
  ricaviLordiCents: Cents;
  /**
   * I ricavi vengono dalle prenotazioni con check-in nel mese, sempre.
   *
   * Resta nel risultato perché un ripiego silenzioso è peggio di un numero
   * mancante: finché questo è `true`, `ricaviLordiCents` è confrontabile con
   * Guesty.
   */
  ricaviDaCheckIn: boolean;
  /**
   * Nessuna prenotazione ha il check-in in questo mese.
   *
   * Non vuol dire che la casa sia stata vuota: un soggiorno lungo cominciato
   * il mese prima riempie le notti e non porta ricavi qui. Chi mostra il
   * numero deve dirlo, altrimenti un mese pieno sembra un mese andato a zero.
   */
  nessunCheckInNelMese: boolean;
  /** Con che convenzione sono contati ricavi e notti: non è la stessa. */
  convenzioni: ConvenzioniConto;
  /**
   * Se il mese è finito, se sta ancora vendendo, o se aspetta gli incassi.
   *
   * `null` quando chi chiama non ha passato la data di oggi: senza orologio
   * non si indovina in che stato è un mese.
   */
  statoMese: StatoMese | null;
  rendiconto: RendicontoCanali;
  righe: RigaProfitto[];
  costiOperativiCents: Cents;

  /** Vista 1 — quanto rende la casa, chiunque incassi. Esclude i trasferimenti. */
  utileCasaCents: Cents;
  /** Vista 2 — quanto resta a te, applicato il rapporto. */
  utileGestoreCents: Cents;
  /** ...e quanto resta al proprietario. I due sommati danno sempre l'utile casa. */
  utileProprietarioCents: Cents;

  /** Il trasferimento fra le parti: la fee di gestione, o il canone. */
  trasferimentoCents: Cents;

  /**
   * Le notti del mese ancora da vendere, e quanto valgono al prezzo esposto.
   *
   * Un mese che non è finito non è un mese di cui mancano i dati: è un mese
   * che sta ancora vendendo, e la domanda che ci si fa sopra è un'altra —
   * non «quanto ho guadagnato» ma «quanto ho già in cassa e quanto può
   * ancora entrare». Senza questi due numeri la risposta su ottobre chiesta a
   * settembre suona come una risposta mancata.
   *
   * Il valore è a listino, quindi è un tetto: le notti si vendono scontate e
   * alcune non si vendono affatto.
   */
  nottiLibere: number;
  /**
   * Le notti che l'host ha chiuso a mano sul gestionale, senza prenotazione
   * dietro: ci sta lui, ci vanno dei lavori, o quelle date non le vende.
   *
   * Stanno fuori da `nottiVendute` **e** da `nottiLibere`, ed è per questo che
   * esistono: finché finivano fra le libere, il «quanto può ancora entrare»
   * contava notti che non sono in vendita, e un mese bloccato per lavori
   * sembrava un mese andato male.
   */
  nottiBloccate: number;
  valoreNottiLibereCents: Cents;
  /**
   * Quando questi numeri sono stati letti dal gestionale, in ISO. `null` se
   * non lo sappiamo.
   *
   * Un conto giusto su dati vecchi, da fuori, è identico a un conto sbagliato:
   * il 13 settembre 2026 i ricavi di settembre erano fermi al giorno 11 e
   * mancavano 586 € di prenotazioni arrivate dopo. Il numero era corretto per
   * quello che Otto sapeva, e non serviva a niente. Da qui in poi la data
   * viaggia insieme alla cifra.
   */
  lettoDalGestionaleIl: string | null;
  /** Quanto dell'utile poggia su numeri inventati da Otto. */
  stimatoCents: Cents;
  /** Positivo: il proprietario ti deve dei soldi. Negativo: glieli devi tu. */
  saldoVersoProprietarioCents: Cents;
  /**
   * Quando l'host ha dichiarato a mano i numeri di questo mese, in ISO.
   *
   * Presente solo sui mesi che il gestionale non conosceva e che l'host ha
   * riempito lui dalla scheda Conti: lì i numeri sono suoi, non letti da
   * Guesty, e chi li mostra — la scheda, Otto in chat, il fascicolo — deve
   * poterlo dire. Assente (o `null`) vuol dire letto dal gestionale.
   */
  dichiaratoIl?: string | null;
}

/**
 * Il conto di **tutto il portafoglio** in un mese.
 *
 * Esiste perché la domanda più naturale che un host si fa — «quanto ho
 * guadagnato in tutto?» — non aveva una risposta sua. Si arrivava sommando N
 * conti, e con quindici immobili voleva dire sedici chiamate al modello e un
 * minuto e mezzo; con cento non ci si arrivava affatto, perché il contesto
 * finisce prima.
 *
 * La somma la fa il codice, in centesimi interi. Mai il modello: un totale
 * giusto per caso è indistinguibile da uno sbagliato finché qualcuno non lo
 * verifica a mano, e nessuno lo verifica.
 */
export interface TotaliPortafoglio {
  ricaviLordiCents: Cents;
  costiOperativiCents: Cents;
  utileCasaCents: Cents;
  utileGestoreCents: Cents;
  utileProprietarioCents: Cents;

  nottiVendute: number;
  nottiDisponibili: number;
  nottiLibere: number;
  nottiBloccate: number;
  valoreNottiLibereCents: Cents;

  /** Quanto del totale poggia su numeri stimati da Otto. */
  stimatoCents: Cents;
  /** Positivo: i proprietari ti devono dei soldi. Negativo: glieli devi tu. */
  saldoVersoProprietariCents: Cents;
}

export interface ProfittoPortafoglio {
  mese: Mese;
  /** Quanti immobili sono entrati nel conto. */
  immobili: number;
  /**
   * Di quanti **non sappiamo niente** in questo mese: zero notti in cache.
   *
   * Senza questo numero il totale sarebbe più basso del vero e nessuno
   * saprebbe perché. «Non sapere» e «aver venduto zero» sono opposte.
   */
  immobiliSenzaCalendario: number;
  totali: TotaliPortafoglio;
  /** Il dettaglio, dal più redditizio al meno. */
  perImmobile: ProfittoImmobile[];
  /**
   * La lettura **più vecchia** fra tutti gli immobili, in ISO.
   *
   * Di un totale interessa l'anello debole: se un immobile su cento è fermo a
   * tre giorni fa, il totale è vecchio di tre giorni, non di un'ora.
   */
  lettoDalGestionaleIl: string | null;
}

/**
 * Il fascicolo di un anno: quello che si manda al commercialista.
 *
 * Una regola sola tiene in piedi tutto il resto di questa struttura: **i mesi
 * chiusi vengono dalla fotografia congelata**, non da un ricalcolo. Il conto di
 * luglio è quello che Otto ha mostrato il 5 agosto, e `definitivo` è di pietra
 * — i trigger vietano UPDATE e DELETE.
 *
 * Il motivo è pratico, non filosofico: un fascicolo che stampato a gennaio e
 * ristampato a marzo dà numeri diversi, senza dire perché, è un documento che
 * il commercialista impara a non usare. Se nel frattempo il gestionale è
 * cambiato, il fascicolo **lo dichiara** invece di cambiare la cifra in
 * silenzio.
 */
export interface MeseDelFascicolo {
  mese: Mese;
  /** `true` se viene dalla fotografia chiusa; `false` se calcolato adesso. */
  definitivo: boolean;
  congelatoIl: string | null;

  nottiVendute: number;
  nottiDisponibili: number;
  ricaviLordiCents: Cents;
  commissioniCents: Cents;
  impostaSoggiornoCents: Cents;
  altriCostiCents: Cents;
  utileCasaCents: Cents;
  /** Quanto di questo mese poggiava su stime, il giorno in cui è stato chiuso. */
  stimatoCents: Cents;

  /**
   * Quanto direbbe il gestionale **oggi**, se diverso dalla fotografia.
   *
   * `null` quando coincidono, che è il caso normale. Quando non coincidono si
   * mostrano tutti e due: la ricevuta resta quella, e la differenza è
   * un'informazione, non un errore da nascondere.
   */
  ricaviOggiCents: Cents | null;

  /**
   * Quando questo mese è stato rettificato, se lo è stato.
   *
   * Una correzione su un conto già chiuso non si nasconde: il documento la
   * dichiara accanto al numero, con la data. È il prezzo di poter correggere
   * un mese di pietra — e il motivo per cui si può.
   */
  rettificatoIl: string | null;
  /**
   * Quando l'host ha dichiarato questo mese a mano, se l'ha fatto.
   *
   * Il commercialista deve sapere quali righe non vengono dal gestionale: un
   * numero dichiarato non è meno vero, ma si verifica su un altro documento.
   */
  dichiaratoIl: string | null;
}

export interface ImmobileDelFascicolo {
  listingId: string;
  nickname: string;
  citta: string | null;
  cin: string | null;
  mesi: MeseDelFascicolo[];
  totali: Omit<
    MeseDelFascicolo,
    | "mese"
    | "definitivo"
    | "congelatoIl"
    | "ricaviOggiCents"
    | "rettificatoIl"
    | "dichiaratoIl"
  >;
}

/**
 * Il quadro di un canale su tutto l'anno.
 *
 * È la sezione che risolve l'ambiguità peggiore del documento. «Incassato» è
 * la somma dei payout — il numero che Guesty chiama ricavi lordi — ma su
 * Airbnb il payout è **già al netto** della commissione, mentre su Booking è
 * lordo e la commissione arriva con una fattura a parte.
 *
 * Letto senza questa distinzione, un mese di sole prenotazioni Airbnb sembra
 * un mese senza commissioni: il commercialista vedrebbe 3.322,96 € incassati
 * e zero costi di canale, quando l'ospite ha pagato 3.932,50 € e 609,54 € se
 * li è presi Airbnb. Qui le due cose stanno su due righe diverse, e il
 * venduto lordo è scritto per esteso.
 */
export interface CanaleDelFascicolo {
  canale: string;
  prenotazioni: number;
  notti: number;
  /** Quello che hanno pagato gli ospiti, prima di qualunque trattenuta. */
  vendutoLordoCents: Cents;
  /** Quello che il canale ha versato sul conto. */
  incassatoCents: Cents;
  /** Tolta dal canale prima di versare: non la paghi, ma è un tuo costo. */
  commissioniTrattenuteCents: Cents;
  /** Fatturata a parte dal canale: la paghi tu, ed è deducibile. */
  commissioniFatturateCents: Cents;
  /** Incassata da te e da versare al Comune. */
  impostaSoggiornoDaVersareCents: Cents;
  /** Incassata e versata dal canale al posto tuo. */
  impostaSoggiornoCanaleCents: Cents;
}

export interface Fascicolo {
  anno: number;
  /** Quando è stato prodotto: un documento contabile porta la sua data. */
  generatoIl: string;
  regimeFiscale: RegimeFiscale | null;
  immobili: ImmobileDelFascicolo[];
  /** Il quadro per canale: dove trattenuto e fatturato si distinguono. */
  canali: CanaleDelFascicolo[];
  totali: Omit<
    MeseDelFascicolo,
    | "mese"
    | "definitivo"
    | "congelatoIl"
    | "ricaviOggiCents"
    | "rettificatoIl"
    | "dichiaratoIl"
  >;
  /** Quante prenotazioni ci sono dietro i numeri di questo fascicolo. */
  prenotazioni: number;
  /** I mesi non ancora chiusi: il fascicolo li dichiara invece di mescolarli. */
  mesiProvvisori: Mese[];
  /** I mesi in cui il gestionale oggi dice un'altra cifra. */
  mesiCambiati: Array<{
    nickname: string;
    mese: Mese;
    primaCents: Cents;
    oggiCents: Cents;
  }>;
  /**
   * I mesi corretti dopo la chiusura, con il motivo.
   *
   * Stanno nel documento perché un numero cambiato dopo la firma è
   * esattamente ciò che un commercialista deve sapere per primo.
   */
  mesiRettificati: Array<{
    nickname: string;
    mese: Mese;
    rettificatoIl: string;
    motivo: string;
  }>;
  /**
   * I mesi i cui numeri li ha dati l'host, non il gestionale.
   *
   * `gestionaleCents` è quello che il gestionale ne sa oggi, quando ne sa
   * qualcosa: di solito un pezzo (le prime settimane su Guesty), a volte
   * niente. Si mostra perché la differenza è un'informazione — e se un giorno
   * il gestionale ne sapesse di più, è qui che si vede.
   */
  mesiDichiarati: Array<{
    nickname: string;
    mese: Mese;
    dichiaratoIl: string;
    gestionaleCents: Cents | null;
  }>;
}

// ── Come si dichiara un rapporto ─────────────────────────────────────────────

/**
 * Le stesse condizioni che stanno nei CHECK della migrazione 0005.
 *
 * Vivono anche qui perché un vincolo che parla solo in SQL parla troppo tardi:
 * l'host deve leggere "un immobile in sublocazione senza canone non lo so
 * calcolare", non un errore del database.
 */
export const RapportoInputSchema = z
  .object({
    tipo: z.enum(TIPI_RAPPORTO),
    controparte: z.string().trim().max(120).nullable().default(null),
    feePercentualeBp: z
      .number()
      .int()
      .min(0)
      .max(10_000)
      .nullable()
      .default(null),
    baseFee: z.enum(BASI_FEE).default("netto_canale"),
    feeFissaCents: z.number().int().min(0).nullable().default(null),
    canoneMensileCents: z.number().int().min(0).nullable().default(null),
    imputazioni: z
      .record(z.string(), z.enum(PARTI))
      .refine(
        (m) =>
          Object.keys(m).every((k) =>
            (CATEGORIE_COSTO as readonly string[]).includes(k),
          ),
        "Categoria di costo sconosciuta",
      )
      .default({}),
    sogliaManutenzioneCents: z.number().int().min(0).nullable().default(null),
    sogliaReclamoCents: z.number().int().min(0).nullable().default(null),
  })
  .refine((r) => r.tipo !== "affitto" || r.canoneMensileCents != null, {
    message:
      "Per un immobile in sublocazione serve il canone che paghi al proprietario.",
    path: ["canoneMensileCents"],
  })
  .refine(
    (r) =>
      r.tipo !== "mandato" ||
      r.feePercentualeBp != null ||
      r.feeFissaCents != null,
    {
      message: "Per un immobile in gestione serve dire come vieni compensato.",
      path: ["feePercentualeBp"],
    },
  );

export type RapportoInput = z.infer<typeof RapportoInputSchema>;

// ── Costi che si ripetono ────────────────────────────────────────────────────

/**
 * Ogni quanto torna un costo.
 *
 * Si conserva quello che l'host ha detto — "500€ all'anno di assicurazione" —
 * e la quota mensile si ricava. Il contrario (chiedergli di dividere per dodici
 * lui) è il genere di attrito che fa smettere di compilare.
 */
export const PERIODICITA = [
  "mensile",
  "bimestrale",
  "trimestrale",
  "semestrale",
  "annuale",
] as const;
export type Periodicita = (typeof PERIODICITA)[number];

const MESI_DI: Record<Periodicita, number> = {
  mensile: 1,
  bimestrale: 2,
  trimestrale: 3,
  semestrale: 6,
  annuale: 12,
};

export function mesiDi(periodicita: Periodicita): number {
  return MESI_DI[periodicita];
}

export const ETICHETTE_PERIODICITA: Record<Periodicita, string> = {
  mensile: "al mese",
  bimestrale: "ogni 2 mesi",
  trimestrale: "ogni 3 mesi",
  semestrale: "ogni 6 mesi",
  annuale: "all'anno",
};

/**
 * Quanto pesa su un mese solo.
 *
 * Per competenza, non per data di pagamento: una bolletta bimestrale da 120€
 * vale 60€ a ciascuno dei due mesi, altrimenti un mese sembra andato male e
 * quello dopo benissimo, e non è successo niente.
 *
 * L'arrotondamento è al centesimo e può far ballare qualche centesimo sull'anno
 * (500€ annui fanno 41,67 × 12 = 500,04). Preferiamo un mese giusto a un anno
 * giusto: è il mese quello che si guarda.
 */
export function quotaMensile(
  importoCents: Cents,
  periodicita: Periodicita,
): Cents {
  return Math.round(importoCents / mesiDi(periodicita));
}

export interface CostoRicorrenteView {
  id: string;
  listingId: string;
  categoria: CategoriaCosto;
  descrizione: string;
  /** Quanto costa *per periodo*, come l'ha detto l'host. */
  importoCents: Cents;
  periodicita: Periodicita;
  dal: IsoDate;
  /** `null` = ancora in corso. */
  al: IsoDate | null;
}

export const CostoRicorrenteInputSchema = z.object({
  categoria: CategoriaCostoSchema,
  descrizione: z.string().trim().min(1).max(200),
  importoCents: z.number().int().min(1),
  periodicita: z.enum(PERIODICITA),
  dal: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  al: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null),
});

export type CostoRicorrenteInput = z.infer<typeof CostoRicorrenteInputSchema>;

// ── La fotografia del mese ───────────────────────────────────────────────────

/**
 * Un P&L mensile messo da parte, e lo stato in cui si trova.
 *
 * Finché è provvisorio si riscrive: è quello che sapevamo, e sapere di più è un
 * miglioramento, non una manomissione. Quando diventa definitivo è di pietra —
 * e il divieto sta nel database, non nel codice.
 *
 * Serve perché oggi il conto di un mese passato **si muove**: confermare quanto
 * costa una pulizia riscrive all'indietro, modificare il rapporto pure, e una
 * risincronizzazione può cambiare il payout di una prenotazione vecchia. Va
 * benissimo finché il conto è vivo. Non va più bene quando quel numero diventa
 * la baseline da cui misurare quanto sei migliorato.
 *
 * Tre campi restano fuori. Le notti ancora libere e il loro valore non hanno
 * senso su un mese finito, e la data di lettura nemmeno: una fotografia vale
 * per quello che sapevamo allora, e dirlo di nuovo oggi sarebbe rumore.
 */
export interface ProfittoCongelato extends Omit<
  ProfittoImmobile,
  | "lettoDalGestionaleIl"
  | "nottiLibere"
  // Una fotografia è di un mese finito: «quante notti restano da vendere» non
  // è più una domanda, e nemmeno «quante ne avevi bloccate».
  | "nottiBloccate"
  | "valoreNottiLibereCents"
  | "statoMese"
> {
  definitivo: boolean;
  /** Quando è stato chiuso. `null` finché è ancora provvisorio. */
  congelatoIl: string | null;
}

/**
 * Il giorno in cui un mese si può considerare chiuso.
 *
 * Non è una scelta di calendario, è una scelta dettata da quando arrivano i
 * soldi: **Booking paga l'host intorno al 3 del mese successivo** — i soggiorni
 * di maggio vengono liquidati il 3 giugno, comunque entro il 5. Chiudere maggio
 * il 31 maggio significherebbe congelare un conto a cui mancano ancora gli
 * incassi che lo riguardano.
 *
 * Il 5 è il primo giorno in cui si è ragionevolmente al sicuro.
 *
 * **Il 5 non è verificato.** Nessuno ha ancora confrontato le date di
 * accredito vere di questo host con il calendario: non c'è una data né un
 * numero dietro a questa costante, e finché non ci sono va letta come una
 * scelta prudente, non come un fatto. Chi la conferma scriva qui la data e
 * l'accredito su cui l'ha vista, come chiede CLAUDE.md.
 */
export const GIORNO_DI_CHIUSURA = 5;

/**
 * In che stato è un mese, dal punto di vista di chi lo guarda.
 *
 * Quattro stati e non due, perché **non sapere** e **non essere ancora
 * finito** sono opposte. In particolare un mese finito ieri non è un mese
 * chiuso: gli incassi del canale devono ancora arrivare, e chiamarlo chiuso
 * significa promettere un numero che si muoverà ancora.
 */
export const STATI_MESE = [
  "futuro",
  "in_corso",
  "in_attesa_incassi",
  "chiuso",
] as const;
export type StatoMese = (typeof STATI_MESE)[number];

export const ETICHETTE_STATO_MESE: Record<StatoMese, string> = {
  futuro: "deve ancora arrivare",
  in_corso: "in corso",
  in_attesa_incassi: "finito, mancano gli incassi del canale",
  chiuso: "chiuso",
};

/**
 * Lo stato di un mese a una certa data.
 *
 * Il confine fra «finito» e «chiuso» è {@link GIORNO_DI_CHIUSURA}, non il 1°
 * del mese dopo: il 2 settembre agosto è finito, ma non è chiuso.
 */
export function statoDelMese(mese: Mese, oggi: IsoDate): StatoMese {
  const corrente = meseDi(oggi);
  if (mese > corrente) return "futuro";
  if (mese === corrente) return "in_corso";
  return meseChiudibile(mese, oggi) ? "chiuso" : "in_attesa_incassi";
}

/**
 * Un mese si può chiudere?
 *
 * Vero dal giorno {@link GIORNO_DI_CHIUSURA} del mese successivo in poi.
 */
export function meseChiudibile(mese: Mese, oggi: IsoDate): boolean {
  const giorno = String(GIORNO_DI_CHIUSURA).padStart(2, "0");
  return oggi >= `${spostaMese(mese, 1)}-${giorno}`;
}

/** L'ultimo mese che, alla data data, si può chiudere. */
export function ultimoMeseChiudibile(oggi: IsoDate): Mese {
  const corrente = meseDi(oggi);
  // Il mese scorso si chiude solo dopo il giorno di chiusura; prima, quello prima.
  return meseChiudibile(spostaMese(corrente, -1), oggi)
    ? spostaMese(corrente, -1)
    : spostaMese(corrente, -2);
}

// ── Il confronto con la baseline ─────────────────────────────────────────────

/**
 * Su cosa poggia un confronto.
 *
 * L'ordine è di qualità decrescente, e non è un dettaglio: dire "sei
 * migliorato del 12%" partendo da una media di mesi non confrontabili è il
 * genere di numero che fa perdere la fiducia in tutto il resto del prodotto.
 */
export const BASI_CONFRONTO = [
  "anno_precedente",
  "media_mesi_chiusi",
  "insufficiente",
] as const;
export type BaseConfronto = (typeof BASI_CONFRONTO)[number];

export const ETICHETTE_BASE_CONFRONTO: Record<BaseConfronto, string> = {
  anno_precedente: "sullo stesso mese dell'anno scorso",
  media_mesi_chiusi: "sulla media dei mesi chiusi",
  insufficiente: "non ancora confrontabile",
};

/**
 * Quanto è andato diversamente da prima — e quanto ci si può credere.
 *
 * `avvertenze` è la parte che conta. Otto è nato da poco su questi immobili e
 * dello storico vero non sa quasi niente: la cache del calendario parte da
 * sette giorni fa, non da un anno fa. Un confronto costruito su quel poco va
 * dato con le sue riserve attaccate, esattamente come la ricevuta dice
 * "inviato, il gestionale non conferma" invece di dire "confermato".
 *
 * Un giorno questo numero servirà a fatturare una percentuale del
 * miglioramento. Quel giorno, ogni riserva taciuta qui diventa una discussione.
 */
export interface ConfrontoBaseline {
  mese: Mese;
  utileGestoreCents: Cents;
  base: BaseConfronto;
  /** Il riferimento, quando esiste. */
  riferimentoCents: Cents | null;
  /** I mesi da cui il riferimento è stato costruito: il conto è ispezionabile. */
  mesiDelRiferimento: Mese[];
  differenzaCents: Cents | null;
  /** Arrotondata al decimo. `null` quando il riferimento è zero o assente. */
  differenzaPercento: number | null;
  /** Vuoto solo quando il confronto regge davvero. */
  avvertenze: string[];
}

/** Sotto questi mesi chiusi non si azzarda nemmeno una media. */
export const MESI_MINIMI_PER_CONFRONTO = 3;

// ── Trattenute dei canali, mese per mese ─────────────────────────────────────

/**
 * Quanto ha trattenuto ogni canale, mese per mese.
 *
 * Nasce da una richiesta pratica: a fine anno servono i numeri delle
 * commissioni, e cercarli fra le fatture di Booking e i rendiconti di Airbnb è
 * un lavoro che nessuno ha voglia di fare due volte.
 *
 * **Non sono le fatture.** Quelle vivono nei portali dei canali, e il
 * gestionale non le espone. Questo è il conto che ci risulta: serve a
 * riconciliare la fattura quando arriva, non a sostituirla — e il testo
 * nell'app lo dice, perché una schermata che si spacciasse per contabilità
 * ufficiale sarebbe la stessa bugia della ricevuta che dice "confermato"
 * quando non lo sa.
 */
export interface TrattenuteCanale {
  canale: string;
  prenotazioni: number;
  /** Il lordo delle notti su cui la trattenuta è stata calcolata. */
  lordoCents: Cents;
  commissioneCents: Cents;
  /** Sul lordo, arrotondata al decimo. `null` se il lordo è zero. */
  percentuale: number | null;
  /**
   * Sappiamo per certo se questo canale versa al lordo o al netto?
   *
   * Airbnb e Booking sì (misurati sull'account vero il 9/9/2026). Vrbo no: su
   * questo host non esiste una prenotazione da cui misurarlo, e la
   * convenzione è cosa di contratto, non di documentazione pubblica. Dove è
   * `false` la percentuale resta un ordine di grandezza, non un numero da
   * dare per certo — e chi la mostra deve dirlo.
   */
  convenzioneVerificata: boolean;
}

export interface MeseTrattenute {
  mese: Mese;
  commissioneCents: Cents;
  canali: TrattenuteCanale[];
}

/**
 * Un mese del grafico: quanto è entrato e dove è andato.
 *
 * Tre fette che sommano a **`vendutoCents`** — trattenute dei canali, costi
 * della casa, quello che resta — perché è così che si legge una barra
 * impilata. Il mese può essere negativo: allora `utileCents` è sotto zero e
 * la barra lo dice.
 *
 * Il totale della barra non è `ricaviCents`: quello è l'incassato, e quello
 * che Airbnb trattiene prima di versare non ci è mai passato dentro. Finché
 * le trattenute della barra escludevano Airbnb e il KPI accanto lo includeva,
 * due mesi identici si confrontavano come «+78%».
 *
 * Due precisazioni, perché un grafico che non torna vale meno di nessun
 * grafico:
 *
 * - dentro `trattenuteCents` c'è anche l'IVA sulla commissione che l'host non
 *   recupera. Non la trattiene il canale, ma nasce dalla commissione e non si
 *   può separare dalle righe del conto, che sono raggruppate per categoria;
 * - le tre fette sommano a `vendutoCents` quando la casa è tua. Con un mandato
 *   o un affitto `utileCents` è quello che resta **a te**, e il trasferimento
 *   verso il proprietario è una quarta fetta che qui non c'è: la barra è
 *   allora più corta del venduto, di quanto è il trasferimento.
 */
export interface AndamentoMese {
  mese: Mese;
  /** Quello che gli ospiti hanno pagato: la somma delle tre fette. */
  vendutoCents: Cents;
  /** L'incassato del mese, il numero di Guesty: payout già al netto di Airbnb. */
  ricaviCents: Cents;
  /** Tutto quello che i canali trattengono, Airbnb compresa. */
  trattenuteCents: Cents;
  costiCents: Cents;
  utileCents: Cents;
  nottiVendute: number;
  nottiDisponibili: number;
}

export interface RiepilogoTrattenute {
  listingId: string;
  nickname: string;
  /** Dal più recente al più vecchio: si guarda l'ultimo, non il primo. */
  mesi: MeseTrattenute[];
  commissioneCents: Cents;
  canali: TrattenuteCanale[];
  /**
   * Le riserve da mostrare. Vuoto quasi mai: la ripartizione per notte e il
   * raggruppamento del canale non coincidono per forza.
   */
  avvertenze: string[];
}

// ── I conti dichiarati dall'host ─────────────────────────────────────────────

/** Il tetto di un importo dichiarato: dieci milioni, oltre è un errore di battitura. */
export const IMPORTO_DICHIARATO_MAX_CENTS = 1_000_000_000;

const ImportoDichiaratoSchema = z
  .number()
  .int("Gli importi sono in centesimi interi.")
  .min(0, "Un importo non può essere negativo.")
  .max(
    IMPORTO_DICHIARATO_MAX_CENTS,
    "Importo troppo grande: controlla le cifre.",
  );

/**
 * I numeri di un mese che il gestionale non conosce, dati dall'host.
 *
 * Le voci sono quelle del fascicolo per il commercialista, e con lo stesso
 * significato — se no un mese dichiarato e uno letto da Guesty, uno sopra
 * l'altro nella stessa tabella, non si potrebbero sommare:
 *
 * - **incassato**: quello che i canali hanno versato per le prenotazioni con
 *   check-in nel mese (la convenzione di Guesty). Airbnb versa già al netto
 *   della sua commissione, quindi quella **non** va ripetuta sotto;
 * - **commissioni pagate a parte**: quelle che il canale fattura dopo, come
 *   Booking;
 * - **imposta di soggiorno** versata al Comune;
 * - **altri costi**: pulizie, biancheria, utenze, manutenzione…
 *
 * Le notti vendute sono facoltative: chi non le sa le lascia vuote, e il mese
 * dice «notti non dichiarate» invece di un'occupazione a zero.
 */
export const ContiDichiaratiSchema = z.object({
  mese: MeseSchema,
  incassatoCents: ImportoDichiaratoSchema,
  commissioniCents: ImportoDichiaratoSchema,
  impostaSoggiornoCents: ImportoDichiaratoSchema,
  altriCostiCents: ImportoDichiaratoSchema,
  nottiVendute: z.number().int().min(0).max(31).nullable(),
  /**
   * Il mese ha già numeri letti dal gestionale, e l'host li vuole sostituire.
   *
   * Senza questo il server rifiuta: sovrascrivere numeri veri con numeri a
   * mano dev'essere una scelta fatta guardando i due, non un effetto
   * collaterale di un tap.
   */
  sostituisci: z.boolean().default(false),
});
export type ContiDichiarati = z.infer<typeof ContiDichiaratiSchema>;

/**
 * L'utile della casa di un mese dichiarato: incassato meno i costi.
 *
 * Sta qui perché lo calcolano in due — l'anteprima mentre l'host scrive, e il
 * server quando salva — e due conti scritti in due posti prima o poi
 * divergono. Il server poi divide l'utile fra te e il proprietario con il
 * rapporto della casa, come in ogni altro mese.
 */
export function utileDichiaratoCents(
  conti: Pick<
    ContiDichiarati,
    | "incassatoCents"
    | "commissioniCents"
    | "impostaSoggiornoCents"
    | "altriCostiCents"
  >,
): Cents {
  return (
    conti.incassatoCents -
    conti.commissioniCents -
    conti.impostaSoggiornoCents -
    conti.altriCostiCents
  );
}
